import { ChangeDetectionStrategy, Component, Inject, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MmDialogData, MmDialogRefService, MmDialogService } from '@metromarkets/components';
import { select, Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { OrderHistoryDialogConfigService } from '../../services/order-history-dialog-config.service';
import { OrderConfirmDialogComponent } from '../order-confirm-dialog/order-confirm-dialog.component';
import { OrderLine } from '../../models';
import { getOrdersReturnReasons, getOrdersReturnReasonsLoading, ReadReturnsReasons, State } from '../../store/returns';
import { METRO_ID_ORGANIZATION } from '~shared/constants/metro-seller.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { CountryCode, FeatureToggleEnum } from '~shared/constants';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { REQUEST } from '@nguniversal/express-engine/tokens';

interface InputData {
  orderLine: OrderLine;
  orderId: string;
}

@Component({
  selector: 'app-return-order-line',
  templateUrl: './return-order-line.component.html',
  styleUrls: ['./return-order-line.component.scss'],
  providers: [OrderHistoryDialogConfigService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnOrderLineComponent implements OnInit {
  public form: FormGroup;
  public returnReasons$ = this.store$.pipe(select(getOrdersReturnReasons));
  public returnReasonsLoading$ = this.store$.pipe(select(getOrdersReturnReasonsLoading));
  public showRefundFeeDisclaimer$ = this.featureFlagService.isFeatureEnabledForCurrentCountry$(FeatureToggleEnum.FF_TMP_ODR_1536_SHOW_REFUND_FEE_IN_OHP)
  
  public readonly metroIdOrganization = METRO_ID_ORGANIZATION;

  public faqUrl = '';
  
  public faqUrlByCountry = {
    [CountryCode.Germany] : 'https://metro-markets.freshdesk.com/de/support/solutions/articles/75000019041-fallen-f%C3%BCr-die-r%C3%BCcksendung-nach-aus%C3%BCbung-des-widerrufsrechts-kosten-f%C3%BCr-mich-an-',
    [CountryCode.Spain] : 'https://support-es.metro-marketplace.eu/es/support/solutions/articles/75000125409-%C2%BFtendr%C3%A9-que-pagar-el-env%C3%ADo-de-la-devoluci%C3%B3n-si-ejerzo-mi-derecho-de-desistimiento-',
    [CountryCode.Italy] : 'https://support-it.metro-marketplace.eu/it/support/solutions/articles/75000086439-in-caso-di-restituzione-sussistono-costi-a-mio-carico-',
    [CountryCode.Portugal] : 'https://support-pt.metro-marketplace.eu/pt-PT/support/solutions/articles/75000125900-terei-de-pagar-o-envio-da-devolu%C3%A7%C3%A3o-depois-de-exercer-o-meu-direito-de-rescis%C3%A3o-/',
    [CountryCode.Netherlands] : 'https://support-nl.metro-marketplace.eu/nl/support/solutions/articles/103000051712-maak-ik-kosten-als-ik-de-goederen-retourneer-',
    [CountryCode.France] : 'https://support-fr.metro-marketplace.eu/fr/support/solutions/articles/103000132844-est-ce-que-je-vais-avoir-des-frais-si-je-retourne-la-marchandise-',
  };

  constructor(@Optional() public dialogRef: MmDialogRefService<ReturnOrderLineComponent>,
              private mmDialogService: MmDialogService,
              @Inject(MmDialogData) public data: InputData,
              private store$: Store<State>,
              private fb: FormBuilder,
              private dialogConfigSrv: OrderHistoryDialogConfigService,
              private readonly featureFlagService: ActiveFeaturesConfigcatService,
              @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
            ) {
  }

  ngOnInit() {
    this.store$.dispatch(new ReadReturnsReasons(this.data.orderLine.orderLineId));
    this.initForm();
    this.faqUrl = this.faqUrlByCountry[getCountryDomainCode(this.webAppBuyerRequest)]
  }


  public onQuantityChanged(quantity: number): void {
    this.form.get('quantity').patchValue(quantity);
  }

  public submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }


    this.openConfirmDialog()
      .afterClosed()
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        this.dialogRef.close(this.form.value);
      });
  }

  private openConfirmDialog() {
    const dialogConfig = this.dialogConfigSrv.returnOrderLineConfirmationConfig;

    return this.mmDialogService.open(OrderConfirmDialogComponent, dialogConfig);
  }

  private initForm() {
    this.form = this.fb.group({
      quantity: [this.data.orderLine.quantity, [Validators.required]],
      returnReason: ['', [Validators.required]],
    });
  }
}
